import {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {createSelector} from 'reselect';
import crypto from 'crypto';
import {KnownVisibilityStates} from 'react-page-visibility';

import {
    selectShowPomodoroWidget,
    useAppDispatch,
    useAppSelector,
    setAppVisibilityAction,
    selectAuthUserToken,
    selectAuthUserEmail,
    selectAuthUserFullName,
    selectAuthUserProfileComplete,
    selectAuthUserTourComplete,
    selectAuthUserMessage,
    selectToast,
    selectWSLastEvent,
    showToastAction,
} from 'store';
import {EWSTypes} from 'models';

declare global {
    interface Window {
        Tawk_API: any;
    }
}

const stateSelectorHandle = createSelector(
    selectShowPomodoroWidget,
    selectAuthUserToken,
    selectAuthUserEmail,
    selectAuthUserFullName,
    selectAuthUserProfileComplete,
    selectAuthUserTourComplete,
    selectAuthUserMessage,
    selectToast,
    selectWSLastEvent,
    (
        showPomodoroWidget,
        authUserToken,
        authUserEmail,
        authUserFullName,
        authUserProfileComplete,
        authUserTourComplete,
        authUserMessage,
        toast,
        lastWsEvent,
    ) => {
        return {
            showPomodoroWidget,
            authUserToken,
            authUserEmail,
            authUserFullName,
            authUserProfileComplete,
            authUserTourComplete,
            authUserMessage,
            toast,
            lastWsEvent,
        };
    },
);

export const useLayout = () => {
    const stateSelector = useCallback(stateSelectorHandle, []);
    const tawkMessengerRef = useRef<any>();
    const {
        showPomodoroWidget,
        authUserToken,
        authUserEmail,
        authUserFullName,
        authUserProfileComplete,
        authUserTourComplete,
        authUserMessage,
        toast,
        lastWsEvent,
    } = useAppSelector(stateSelector);
    const location = useLocation();
    const dispatch = useAppDispatch();

    /**
     * Handle window visibility change
     * @param {boolean} isVisible
     * @param {KnownVisibilityStates} visibilityState
     */
    const handleVisibilityChange = useCallback(
        (isVisible: boolean, visibilityState: KnownVisibilityStates) => {
            dispatch(
                setAppVisibilityAction({
                    isVisible,
                    visibilityState,
                }),
            );
        },
        [dispatch],
    );

    useEffect(() => {
        if (
            lastWsEvent?.type === EWSTypes['global_invite_to_join_room'] &&
            toast?.publicSessionId &&
            // Must be an update for the SAME room the toast is showing --
            // otherwise an unrelated public room emptying out force-closes
            // whatever invite toast happens to be on screen right now.
            lastWsEvent?.data?.room?.id === toast.publicSessionId &&
            !lastWsEvent?.data?.room?.participants_count
        ) {
            dispatch(
                showToastAction({
                    open: false,
                    vertical: 'top',
                    horizontal: 'center',
                    subTitle: '',
                    title: '',
                }),
            );
        }
    }, [dispatch, lastWsEvent, toast?.publicSessionId]);

    useEffect(() => {
        if (
            lastWsEvent?.type === EWSTypes['group_room_invite_to_join_room'] &&
            toast?.groupSessionId &&
            // Same guard as above, for group rooms: only dismiss the toast
            // if this update is actually about the room it's showing.
            lastWsEvent?.data?.room?.id === toast.groupSessionId &&
            !lastWsEvent?.data?.room?.participant_count
        ) {
            dispatch(
                showToastAction({
                    open: false,
                    vertical: 'top',
                    horizontal: 'center',
                    subTitle: '',
                    title: '',
                }),
            );
        }
    }, [dispatch, lastWsEvent, toast?.groupSessionId]);

    useEffect(() => {
        try {
            const tawkto = window?.Tawk_API;

            if (authUserToken && authUserEmail && authUserFullName) {
                const hash = crypto
                    .createHmac(
                        'sha256',
                        process.env.REACT_APP_TAWKTO_JS_API_KEY || '',
                    )
                    .update(authUserEmail || '')
                    .digest('hex');

                tawkto.onLoad = function () {
                    tawkto.setAttributes(
                        {
                            name: authUserFullName,
                            email: authUserEmail,
                            hash: hash,
                        },
                        function (error: any) {
                            console.error({error});
                        },
                    );
                };
            }
        } catch (error) {
            console.error({error});
        }
    }, [authUserEmail, authUserFullName, authUserToken, location.pathname]);

    return {
        tawkMessengerRef,
        handleVisibilityChange,
        authUserToken,
        authUserProfileComplete,
        authUserTourComplete,
        location,
        showPomodoroWidget,
        authUserMessage,
    };
};
